import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import { sortBy } from "lodash";
import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import {
  ApplicantSubmissions,
  ApplicantSubs,
} from "types/gql-types";
import { InterestStage, statusFromStage } from "model/interest/status";
import { LoadingSpinner } from "components/graphql-views";
import Application from "./client-application";


const QUERY_APPLICATIONS = gql`
  query ApplicantSubmissions {
    clientInterests {
      id
      status {
        name
        changedAt
      }
      submittedAt
      updateRequest {
        code
        isActive
      }
      interestList {
        id
        name
        isArchived
        property {
          id
          email
          phone
          name
          reinstatementDays
        }
      }
    }
  }
`;
const QUERY_SUBMISSIONS = gql`
  query ApplicantSubs {
    submissions {
      id
      submittedAt
      form {
        type
        property {
          id
          email
          phone
          name
          interestList {
            id
            name
            isArchived
          }
        }
      }
    }
  }
`;

type ClientApplicationsParams = {setInterestsLength: React.Dispatch<React.SetStateAction<number>>}

const ClientApplications: React.FC<ClientApplicationsParams> = ({ setInterestsLength }) => {
  const result = useQuery<ApplicantSubmissions>(QUERY_APPLICATIONS, { fetchPolicy: "no-cache" });
  useEffect(() => {
    if (result.data?.clientInterests) {
      setInterestsLength(result.data.clientInterests.length);
    }
  }, [result]);
  if (result.loading || result.error || !result.data) return null;
  const { clientInterests } = result.data;

  return <>{sortBy(
    clientInterests,
    (interest) => statusFromStage(interest.status.name as InterestStage),
    (interest) => interest.submittedAt,
  ).map((application) => (
    <Application
      status={statusFromStage(application.status.name)}
      statusChangedAt={application.status.changedAt ?? ""}
      key={application.id}
      data={application}
      onApplicationRemoved={() => result.refetch()}
      interestList={application.interestList}
      property={application.interestList.property}
      submittedAt={application.submittedAt}
      type="application"
    />
  ))}</>;
};

type UserSubmissionsParams = {setSubmissionsLength: React.Dispatch<React.SetStateAction<number>>}
const UserSubmissions: React.FC<UserSubmissionsParams> = ({ setSubmissionsLength }) => {
  const result = useQuery<ApplicantSubs>(QUERY_SUBMISSIONS, { fetchPolicy: "no-cache" });

  useEffect(() => {
    if (result.data?.submissions) {
      setSubmissionsLength(result.data.submissions.length);
    }
  }, [result]);
  if (result.loading || result.error || !result.data) return null;
  let { data: { submissions } } = result;

  // Only incomplete submissions are shown:
  submissions = submissions.filter(({ submittedAt }) => submittedAt === null);
  return <>
    {submissions.map((submission) => (
      <Application
        status="Draft"
        statusChangedAt=""
        key={submission.id}
        type="submission"
        property={{ ...submission.form.property, reinstatementDays: 0 }}
        interestList={submission.form.property.interestList}
        data={submission}
        submittedAt={submission.submittedAt ? Number(submission.submittedAt) : null}
        onApplicationRemoved={() => result.refetch()}
      />))}</>;
};

const Applications: React.FC = () => {
  const [submissionsLength, setSubmissionsLength] = useState(-1);
  const [interestsLength, setInterestsLength] = useState(-1);
  return (
    <div style={{ margin: 16 }}>
      <Grid container spacing={2}>
        <UserSubmissions setSubmissionsLength={setSubmissionsLength} />
        <ClientApplications setInterestsLength={setInterestsLength}/>
        {(submissionsLength === -1 || interestsLength === -1) && (<LoadingSpinner />)}
        {submissionsLength === 0 && interestsLength === 0 && (
          <div style={{ textAlign: "center" }}>No Interest Submissions Available.</div>)}
      </Grid>
    </div>
  );
};

export default Applications;
